import { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import ArticleCard from "@/components/feature/ArticleCard";
import { useSeo } from "@/hooks/useSeo";
import { articles } from "@/mocks/articles";
import { site } from "@/mocks/site";
import ReadingProgress from "./components/ReadingProgress";
import ArticleToc from "./components/ArticleToc";

export default function ArticleDetail() {
  const { slug } = useParams();
  const article = articles.find((a) => a.slug === slug);

  const baseUrl = `${window.location.origin}${__BASE_PATH__.replace(/\/$/, "")}`;

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [slug]);

  const tocItems = useMemo(
    () => (article ? article.sections.map((s, i) => ({ id: `sec-${i}`, heading: s.heading })) : []),
    [article],
  );

  const related = useMemo(() => {
    if (!article) return [];
    const sameCategory = articles.filter((a) => a.category === article.category && a.slug !== article.slug);
    const others = articles.filter((a) => a.category !== article.category && a.slug !== article.slug);
    return [...sameCategory, ...others].slice(0, 3);
  }, [article]);

  const jsonLd = useMemo(() => {
    if (!article) return undefined;
    return [
      {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: article.title,
        description: article.excerpt,
        image: article.cover,
        datePublished: article.date,
        dateModified: article.date,
        inLanguage: "zh-CN",
        author: { "@type": "Organization", name: article.author },
        publisher: { "@type": "Organization", name: site.name },
        articleSection: article.categoryName,
        keywords: article.tags.join(","),
        mainEntityOfPage: `${baseUrl}/article/${article.slug}`,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${baseUrl}/` },
          { "@type": "ListItem", position: 2, name: article.categoryName, item: `${baseUrl}/#categories` },
          { "@type": "ListItem", position: 3, name: article.title, item: `${baseUrl}/article/${article.slug}` },
        ],
      },
    ];
  }, [article, baseUrl]);

  useSeo({
    title: article ? `${article.title} - 瓜网` : "内容不存在 - 瓜网",
    description: article ? article.excerpt : "该内容可能已被移除或链接有误。",
    keywords: article ? `${article.tags.join(",")},瓜网,娱乐资讯` : "瓜网",
    path: article ? `/article/${article.slug}` : "/",
    image: article?.cover,
    jsonLd,
  });

  if (!article) {
    return (
      <div id="top" className="min-h-screen bg-background-50">
        <SiteHeader />
        <main className="mx-auto flex w-full max-w-[1400px] flex-col items-center px-4 py-24 text-center md:px-6">
          <i className="ri-file-unknow-line text-5xl text-background-400" aria-hidden="true"></i>
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">没有找到这篇内容</h1>
          <p className="mt-2 text-sm text-foreground-600">它可能已经下线，或者链接被改动了。</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line" aria-hidden="true"></i>
            返回首页
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div id="top" className="min-h-screen bg-background-50">
      <ReadingProgress />
      <SiteHeader />

      <main>
        <div className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-6 md:py-10">
            <nav aria-label="面包屑" className="mb-5 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
              <Link to="/" className="hover:text-primary-700">首页</Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
              <Link to="/#categories" className="hover:text-primary-700">{article.categoryName}</Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
              <span className="line-clamp-1 text-foreground-700">{article.title}</span>
            </nav>

            <span className="inline-block rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
              {article.categoryName}
            </span>
            <h1 className="mt-3 max-w-4xl font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-[2.6rem] md:leading-[1.2]">
              {article.title}
            </h1>
            <p className="mt-4 max-w-3xl text-base leading-relaxed text-foreground-700">{article.excerpt}</p>

            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-3-line" aria-hidden="true"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" aria-hidden="true"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line" aria-hidden="true"></i>
                阅读约 {article.readTime}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-fire-line text-accent-500" aria-hidden="true"></i>
                热度 {article.heat.toLocaleString("zh-CN")}
              </span>
            </div>
          </div>
        </div>

        <div className="mx-auto w-full max-w-[1400px] px-4 py-10 md:px-6 md:py-14">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <article className="lg:col-span-8">
              <div className="h-64 w-full overflow-hidden rounded-lg md:h-96">
                <img
                  src={article.cover}
                  alt={`${article.title} · 瓜网${article.categoryName}`}
                  title={`${article.title} · 瓜网${article.categoryName}`}
                  className="h-full w-full object-cover object-top"
                />
              </div>

              <div className="mt-8 space-y-10">
                {article.sections.map((section, index) => (
                  <section key={section.heading} id={`sec-${index}`} className="scroll-mt-28">
                    <h2 className="flex items-start gap-3 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                      <span className="mt-1 text-base text-primary-600">{String(index + 1).padStart(2, "0")}</span>
                      {section.heading}
                    </h2>
                    <div className="mt-4 space-y-4">
                      {section.paragraphs.map((text, pIndex) => (
                        <p key={pIndex} className="text-base leading-[1.9] text-foreground-800">
                          {text}
                        </p>
                      ))}
                    </div>
                  </section>
                ))}
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span key={tag} className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5">
                <div className="flex items-center gap-4">
                  <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-primary-100">
                    <i className="ri-quill-pen-line text-xl text-primary-700" aria-hidden="true"></i>
                  </span>
                  <div>
                    <p className="font-heading text-base font-bold text-foreground-950">{article.author}</p>
                    <p className="mt-0.5 text-sm text-foreground-600">
                      瓜网编辑部 · 关注娱乐产业与流行文化的公开信息整理。
                    </p>
                  </div>
                </div>
              </div>
            </article>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-28">
                {tocItems.length > 0 && <ArticleToc items={tocItems} />}

                <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-5">
                  <h3 className="font-heading text-base font-bold text-foreground-950">相关推荐</h3>
                  <ul className="mt-4 space-y-4">
                    {related.map((item) => (
                      <li key={item.slug}>
                        <Link to={`/article/${item.slug}`} className="group flex gap-3">
                          <span className="h-16 w-20 flex-shrink-0 overflow-hidden rounded-md">
                            <img
                              src={item.cover}
                              alt={`${item.title} · 瓜网${item.categoryName}`}
                              title={`${item.title} · 瓜网${item.categoryName}`}
                              className="h-full w-full object-cover object-top"
                            />
                          </span>
                          <span className="min-w-0">
                            <span className="line-clamp-2 font-heading text-sm font-bold leading-snug text-foreground-900 group-hover:text-primary-700">
                              {item.title}
                            </span>
                            <span className="mt-1 block text-xs text-foreground-500">{item.categoryName}</span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </aside>
          </div>
        </div>

        <section className="border-t border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1400px] px-4 py-12 md:px-6 md:py-16">
            <div className="mb-8 flex items-end justify-between border-b border-background-200 pb-5">
              <h2 className="font-heading text-2xl font-bold text-foreground-950">继续阅读</h2>
              <Link to="/#latest" className="text-sm font-medium text-primary-700 hover:text-primary-800">
                全部资讯 <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((item) => (
                <ArticleCard key={item.slug} article={item} />
              ))}
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}